/**
 * Tolerance-cost ranking (PRD §4.6 tier 3).
 *
 * Variance share tells you where the spread comes from. This tells you what to
 * do about it, which is a different question and the one that actually gets
 * asked in a design review. It is opt-in because it costs one extra Monte Carlo
 * run per variable — the button states that cost before you pay it.
 *
 * Both directions are shown. Loosening is not a mistake to be warned about: a
 * tolerance that costs money and buys no yield is exactly what a cost-reduction
 * pass is looking for.
 */

import { useState } from 'react';
import type { Project } from '../../engine/model.ts';
import { rankTolerances, type ToleranceRanking, type ToleranceOption } from '../../engine/tolerance.ts';
import { eng, percent, ppm, sig, count } from '../format.ts';
import { Info } from '../Info.tsx';

interface Props {
  project: Project;
  outputIndex: number;
  unit?: string;
}

const PROBE_ITERATIONS = 50_000;

function DeltaBar({ option, scale }: { option: ToleranceOption; scale: number }) {
  const magnitude = Math.abs(option.stdDevDelta);
  const improving = option.stdDevDelta < 0;
  return (
    <span className="delta-cell">
      <span className="delta-track">
        <span
          className={improving ? 'delta-bar improving' : 'delta-bar worsening'}
          style={{ width: `${scale > 0 ? (magnitude / scale) * 100 : 0}%` }}
          aria-hidden="true"
        />
      </span>
      <span className="delta-value">
        {improving ? '−' : '+'}
        {percent(magnitude, 1)}
      </span>
    </span>
  );
}

function RankTable({
  title,
  hint,
  options,
  ranking,
  unit,
}: {
  title: string;
  hint: string;
  options: ToleranceOption[];
  ranking: ToleranceRanking;
  unit?: string;
}) {
  if (options.length === 0) return null;
  const scale = Math.max(...options.map((o) => Math.abs(o.stdDevDelta)), 1e-12);
  const hasYield = ranking.baselineYield !== undefined;

  return (
    <div className="rank-block">
      <h4>{title}</h4>
      <p className="section-hint">{hint}</p>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th scope="col">Variable</th>
              <th scope="col">Change in σ</th>
              <th scope="col">New σ</th>
              {hasYield && <th scope="col">New yield</th>}
              {hasYield && <th scope="col">Δ PPM</th>}
              <th scope="col">Linear model</th>
            </tr>
          </thead>
          <tbody>
            {options.map((o) => (
              <tr key={o.varId}>
                <td>
                  <code>{o.varId}</code>
                </td>
                <td>
                  <DeltaBar option={o} scale={scale} />
                </td>
                <td>{eng(o.stdDev, 4, unit)}</td>
                {hasYield && <td>{percent(o.yieldPoint ?? NaN, 3)}</td>}
                {hasYield && (
                  <td className={(o.ppmDelta ?? 0) < 0 ? 'good-text' : undefined}>
                    {o.ppmDelta === undefined
                      ? '—'
                      : `${o.ppmDelta < 0 ? '−' : '+'}${ppm(Math.abs(o.ppmDelta))}`}
                  </td>
                )}
                <td>
                  {o.predictionHolds ? (
                    <span className="muted">agrees</span>
                  ) : (
                    <span className="bad" title={`Linear model predicts σ = ${sig(o.predictedStdDev, 4)}`}>
                      differs
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function TolerancePanel({ project, outputIndex, unit }: Props) {
  const [ranking, setRanking] = useState<ToleranceRanking | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const varCount = project.variables.filter((v) => v.distribution.kind !== 'fixed').length;

  const run = () => {
    setBusy(true);
    setError(null);
    // Yield to the browser so the button can show its busy state before the
    // main thread is occupied. This work is not in a Worker: it is a sequence
    // of full analyses whose intermediate results are discarded, and shipping
    // each probe's project across the boundary would cost more than it saves.
    setTimeout(() => {
      try {
        setRanking(rankTolerances(project, outputIndex, { iterations: PROBE_ITERATIONS }));
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(false);
      }
    }, 20);
  };

  return (
    <section className="tolerance-panel">
      <div className="chart-title">
        <span className="heading-text">What should I tighten? <Info topic="tolerance-ranking" /></span>
        {ranking && (
          <button type="button" className="link-btn" onClick={run} disabled={busy}>
            Recompute
          </button>
        )}
      </div>

      {!ranking && (
        <div className="tolerance-cta">
          <p>
            Halve each tolerance in turn and measure what it actually buys, in output σ and
            in yield. Variance share says where the spread comes from; this says what to do
            about it.
          </p>
          <button type="button" className="primary" onClick={run} disabled={busy || varCount === 0}>
            {busy ? 'Running…' : 'Rank tolerances'}
          </button>
          <span className="muted small">
            {count(varCount * 2 + 1)} runs of {count(PROBE_ITERATIONS)} iterations
          </span>
        </div>
      )}

      {error && <p className="callout bad">{error}</p>}

      {ranking && (
        <>
          <p className="chart-note">
            Baseline σ {eng(ranking.baselineStdDev, 4, unit)}
            {ranking.baselineYield !== undefined && (
              <> · yield {percent(ranking.baselineYield, 3)}</>
            )}
            {' · '}
            each probe uses the same seed and iteration count as the baseline, so a difference
            is the tolerance change and not sampling noise.
          </p>

          {ranking.notes.map((n, i) => (
            <p key={i} className="callout warn">
              {n}
            </p>
          ))}

          <RankTable
            title="Tighten — halve the tolerance"
            hint="Best improvement first. This is the order to spend money in."
            options={ranking.tighten}
            ranking={ranking}
            unit={unit}
          />

          <RankTable
            title="Loosen — double the tolerance"
            hint="Least damage first. A tolerance near the top costs money and buys almost nothing."
            options={ranking.loosen}
            ranking={ranking}
            unit={unit}
          />
        </>
      )}
    </section>
  );
}
